/**
 * Remove Member from Community Hook
 * Handles removing a member from a community with proper cache invalidation
 */

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { removeRitualistFromCommunity } from "../services/community.service"

interface UseRemoveMemberParams {
  communityId: string
}

/**
 * Remove Member Mutation Hook
 * Removes a member from a community and handles cache invalidation
 */
export function useRemoveMember({ communityId }: UseRemoveMemberParams) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (userId: string) => removeRitualistFromCommunity(communityId, userId),
    onSuccess: data => {
      // Invalidate all community members queries (both regular and infinite)
      queryClient.invalidateQueries({
        queryKey: ["communityMembers", communityId],
      })
      queryClient.invalidateQueries({
        queryKey: ["community-members", communityId],
      })
      // Invalidate community ritualists (member may have ritualist role)
      queryClient.invalidateQueries({
        queryKey: ["communityRitualists", communityId],
      })
      // Invalidate community detail (member count may have changed)
      queryClient.invalidateQueries({
        queryKey: ["community", communityId],
      })
      // Invalidate community statistics
      queryClient.invalidateQueries({
        queryKey: ["community-statistics", communityId],
      })
      // Invalidate profile statistics (communities count may change)
      queryClient.invalidateQueries({
        queryKey: ["profileStatistics"],
      })
      queryClient.invalidateQueries({
        queryKey: ["publicProfileStatistics"],
      })
      toast.success(data.message || "Member removed from community")
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || "Failed to remove member")
    },
  })
}
